export const GROUP_TYPE = 'evp_configurator_product_group';
export const GROUP_NAMESPACE = 'evp_configurator';
export const GROUP_KEY = 'product_group';
export const DRAFT_GROUP_KEY = 'product_group_draft';
export const GROUP_FIELDS = [
  {key: 'name', name: 'Name', type: 'single_line_text_field'},
  {key: 'members', name: 'Members', type: 'list.product_reference'},
  {key: 'configuration', name: 'Configuration', type: 'json'},
];

export function emptySwitcher(product = {}) {
  return {internalName: product.title || '', baseHandle: product.handle || '', groupId: '', switcherLabel: '', style: 'hidden',
    products: Array.from({length: 5}, (_, index) => ({productId: index === 0 ? product.id || '' : '', title: index === 0 ? product.title || '' : '', handle: index === 0 ? product.handle || '' : '', label: ''}))};
}

export function objectFingerprint(object) {
  return object ? JSON.stringify((object.fields || []).map(({key, value}) => [key, value]).sort(([a], [b]) => a.localeCompare(b))) : null;
}

export function groupMembers(object) {
  const field = object?.fields?.find(({key}) => key === 'members');
  if (!field?.value) return [];
  return JSON.parse(field.value).map((id) => {
    const product = field.references?.nodes?.find((item) => item.id === id);
    return {productId: id, title: product?.title || '', handle: product?.handle || ''};
  });
}

// Membership is authoritative; switcher configuration owns only labels/order.
export function reconcileSwitcher(value, members) {
  const ordered = value.products.filter((item) => members.some((member) => member.productId === item.productId));
  const products = [...ordered, ...members.filter((member) => !ordered.some((item) => item.productId === member.productId))]
    .map((item) => ({...item, ...members.find((member) => member.productId === item.productId), label: item.label ?? item.title ?? ''}));
  return {...value, products};
}

export function switcherFromMetaobject(object) {
  const fields = Object.fromEntries((object?.fields || []).map((field) => [field.key, field]));
  if (fields.configuration?.value) {
    const config = JSON.parse(fields.configuration.value);
    if (config.schemaVersion !== 1 || !Array.isArray(config.products) || config.products.length > 5) throw new Error('Unsupported product switcher configuration.');
    const members = fields.members?.references?.nodes || [];
    const products = config.products.map((slot) => {
      const member = members.find(({id}) => id === slot.productId);
      return {...slot, title: member?.title || '', handle: member?.handle || ''};
    });
    return {internalName: fields.name?.value || '', baseHandle: config.baseHandle || object.handle, groupId: config.groupId || object.id,
      switcherLabel: config.switcherLabel || '', description: config.description || '', style: config.style ?? 'default', detached: config.detached === true,
      products: [...products, ...emptySwitcher().products].slice(0, 5)};
  }
  return {
    internalName: fields.internal_name?.value || '', baseHandle: object?.handle || '', groupId: object?.id || '', switcherLabel: fields.switcher_label?.value || '', style: 'default',
    products: Array.from({length: 5}, (_, index) => {
      const field = fields[`product_${index + 1}`];
      return {productId: field?.value || '', title: field?.reference?.title || '', handle: field?.reference?.handle || '', label: fields[`product_${index + 1}_label`]?.value || ''};
    }),
  };
}

export function validateSwitcher(value, publish = false) {
  if (!value || !Array.isArray(value.products) || value.products.length > 5) throw new Error('A switcher supports up to five products.');
  if (typeof value.internalName !== 'string' || !value.internalName.trim()) throw new Error('Enter a group name.');
  if (typeof value.baseHandle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(value.baseHandle)) throw new Error('The group needs a valid creating product handle.');
  if (typeof value.switcherLabel !== 'string' || typeof value.groupId !== 'string') throw new Error('Invalid group configuration.');
  if (value.style !== undefined && !['hidden', 'default', 'boxes', 'form', 'navigation'].includes(value.style)) throw new Error('Switcher style must be hidden, form, or navigation.');
  const ids = new Set();
  value.products.forEach((slot, index) => {
    if (typeof slot?.productId !== 'string' || typeof slot?.label !== 'string') throw new Error(`Product ${index + 1} has invalid values.`);
    if (slot.description !== undefined && typeof slot.description !== 'string') throw new Error(`Product ${index + 1} has an invalid description.`);
    if (slot.productId && !/^gid:\/\/shopify\/Product\/\d+$/.test(slot.productId)) throw new Error(`Product ${index + 1} is invalid.`);
    if (slot.productId && ids.has(slot.productId)) throw new Error('Each product can only appear once.');
    if (slot.productId) ids.add(slot.productId);
    if (publish && slot.productId && !slot.label.trim()) throw new Error(`Enter a label for Product ${index + 1}.`);
    if (publish && !slot.productId && slot.label.trim()) throw new Error(`Choose a product for label ${index + 1}, or clear its label.`);
  });
  if (publish && (!value.switcherLabel.trim() || ids.size < 2)) throw new Error('Publishing requires a switcher label and at least two products.');
  return [...ids];
}

export function switcherFields(value, publish = false, revision = '') {
  const members = validateSwitcher(value, publish);
  const configuration = {schemaVersion: 1, groupId: value.groupId, baseHandle: value.baseHandle, revision,
    switcherLabel: value.switcherLabel.trim(), description: (value.description || '').trim(), style: value.style ?? 'default', ...(value.detached ? {detached: true} : {}),
    products: value.products.filter(({productId}) => productId).map(({productId, label, description}) => ({productId, label: label.trim(), description: (description || '').trim()}))};
  return [{key: 'name', value: value.internalName.trim()}, {key: 'members', value: JSON.stringify(members)}, {key: 'configuration', value: JSON.stringify(configuration)}];
}

export function sameSwitcher(left, right) {
  const comparable = (value) => value && ({internalName: value.internalName, switcherLabel: value.switcherLabel, description: value.description || '', style: value.style ?? 'default',
    products: value.products.filter(({productId, label}) => productId || label).map(({productId, label, description}) => ({productId, label, description: description || ''}))});
  return JSON.stringify(comparable(left)) === JSON.stringify(comparable(right));
}

export function parseSwitcherDraft(field) {
  if (!field) return null;
  const draft = typeof field.jsonValue === 'object' && field.jsonValue !== null ? field.jsonValue : JSON.parse(field.value);
  if (draft.schemaVersion !== 1 || !draft.value || !Array.isArray(draft.baseline)) throw new Error('Unsupported product switcher draft.');
  validateSwitcher(draft.value);
  return draft;
}
